import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Mail, 
  FileText, 
  CheckCircle, 
  AlertTriangle,
  Eye,
  Download,
  Trash2,
  Lock,
  ArrowRight,
  Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'react-hot-toast';
import Navbar from '../components/layout/Navbar';
import gdprService, { GDPRRequestData } from '../services/gdprService';

const PublicGDPRPage: React.FC = () => {
  const [requestForm, setRequestForm] = useState({
    email: '',
    request_type: 'access',
    description: '',
    full_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submitGDPRRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!requestForm.email || !requestForm.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(requestForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Name validation (at least 2 words)
    const nameParts = requestForm.full_name.trim().split(' ');
    if (nameParts.length < 2 || nameParts.some(part => part.length < 1)) {
      toast.error('Please enter your full name (first and last name)');
      return;
    }

    setLoading(true);
    try {
      const gdprData: GDPRRequestData = {
        user_email: requestForm.email.toLowerCase().trim(),
        full_name: requestForm.full_name.trim(),
        request_type: requestForm.request_type,
        description: requestForm.description?.trim() || undefined
      };

      console.log('🔒 Submitting GDPR request with enhanced validation');
      const response = await gdprService.submitPublicGDPRRequest(gdprData);

      if (response.success) {
        setSubmitted(true);
        toast.success(response.message || 'GDPR request submitted successfully. We will verify your identity and respond within 30 days.');
      } else {
        toast.error('Failed to submit GDPR request. Please try again.');
      }
    } catch (error: any) {
      console.error('GDPR request submission error:', error);
      
      let errorMessage = 'Failed to submit GDPR request. Please try again.';
      
      if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Service temporarily unavailable. Please try again later or contact support.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many requests. Please wait a few minutes before trying again.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const requestTypes = [
    { value: 'access', label: 'Data Access Request', desc: 'Request a copy of all personal data we hold about you' },
    { value: 'portability', label: 'Data Portability Request', desc: 'Export your data in a machine-readable format' },
    { value: 'rectification', label: 'Data Rectification Request', desc: 'Correct inaccurate or incomplete personal data' },
    { value: 'erasure', label: 'Data Erasure Request', desc: 'Request deletion of your personal data ("right to be forgotten")' },
    { value: 'restrict', label: 'Restrict Processing Request', desc: 'Limit how we process your personal data' },
    { value: 'object', label: 'Object to Processing Request', desc: 'Object to certain types of data processing' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <Navbar />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Request Submitted Successfully
            </h1>
            
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Thank you for your GDPR request. We have received your submission and will respond within 30 days as required by law.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 max-w-2xl mx-auto">
              <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-blue-800 space-y-2 text-left">
                <li>• We will verify your identity to protect your privacy</li>
                <li>• Our privacy team will process your request</li>
                <li>• You will receive a response via email within 30 days</li>
                <li>• For urgent matters, contact us at info@quixocial.com</li>
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button variant="outline" className="px-6 py-3">
                  Back to Home
                </Button>
              </Link>
              <Link to="/privacy-policy">
                <Button className="px-6 py-3">
                  <FileText className="w-4 h-4 mr-2" />
                  Read Privacy Policy
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            GDPR Data Rights Request
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Exercise your privacy rights under GDPR and other data protection regulations. 
            Submit a request to access, correct, or delete your personal data.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Side - Your Rights */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Privacy Rights</h2>
              
              <div className="space-y-4">
                {[
                  { icon: Eye, title: 'Right to Access', desc: 'Get a copy of all personal data we hold about you', color: 'blue' },
                  { icon: Download, title: 'Data Portability', desc: 'Export your data in a machine-readable format', color: 'green' },
                  { icon: FileText, title: 'Right to Rectification', desc: 'Correct inaccurate or incomplete personal data', color: 'orange' },
                  { icon: Trash2, title: 'Right to Erasure', desc: 'Request deletion of your personal data', color: 'red' },
                  { icon: Lock, title: 'Restrict Processing', desc: 'Limit how we process your personal data', color: 'purple' },
                  { icon: AlertTriangle, title: 'Object to Processing', desc: 'Object to certain types of data processing', color: 'yellow' }
                ].map((right, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      right.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                      right.color === 'green' ? 'bg-green-100 text-green-600' :
                      right.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                      right.color === 'red' ? 'bg-red-100 text-red-600' :
                      right.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                      'bg-yellow-100 text-yellow-600'
                    }`}>
                      <right.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{right.title}</h3>
                      <p className="text-sm text-gray-600">{right.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Need Help?
              </h3>
              <p className="text-sm text-blue-800 mb-3">
                For questions about your privacy rights or this form, contact our Data Protection Officer:
              </p>
              <div className="space-y-1 text-sm text-blue-800">
                <p><strong>Email:</strong> info@quixocial.com</p>
                <p><strong>Response Time:</strong> Within 30 days (as required by law)</p>
              </div>
            </div>
          </div>

          {/* Right Side - Request Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Submit Your Request</h2>
            
            <form onSubmit={submitGDPRRequest} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <Input
                  type="text"
                  value={requestForm.full_name}
                  onChange={(e) => setRequestForm({ ...requestForm, full_name: e.target.value })}
                  placeholder="Enter your full name"
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={requestForm.email}
                  onChange={(e) => setRequestForm({ ...requestForm, email: e.target.value })}
                  placeholder="Enter your email address"
                  required
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  We'll use this to verify your identity and send our response
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Request Type *
                </label>
                <div className="space-y-3">
                  {requestTypes.map((type) => (
                    <label key={type.value} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="request_type"
                        value={type.value}
                        checked={requestForm.request_type === type.value}
                        onChange={(e) => setRequestForm({ ...requestForm, request_type: e.target.value })}
                        className="mt-1 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="font-medium text-gray-900">{type.label}</div>
                        <div className="text-sm text-gray-600">{type.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Details (Optional)
                </label>
                <Textarea
                  value={requestForm.description}
                  onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                  placeholder="Provide any additional details about your request..."
                  rows={4}
                  className="w-full resize-none"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-1">Identity Verification Required</h4>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      By submitting this request, you confirm that you are the data subject or have authorization to act on their behalf. 
                      <strong> We will verify your identity before processing any request.</strong> This may include:
                    </p>
                    <ul className="text-xs text-amber-700 mt-2 space-y-1">
                      <li>• Email verification to the provided address</li>
                      <li>• Additional identity verification documents</li>
                      <li>• Confirmation of account ownership (if applicable)</li>
                    </ul>
                    <p className="text-xs text-amber-700 mt-2">
                      <strong>Fraudulent requests are illegal and will be reported.</strong> We will respond within 30 days as required by GDPR.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-6 rounded-xl font-semibold shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit GDPR Request
                  </>
                )}
              </Button>
            </form>

            {/* Already have an account */}
            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600 mb-3">
                Already have an account? Access advanced privacy tools:
              </p>
              <Link to="/app/gdpr-compliance">
                <Button variant="outline" className="w-full">
                  <Shield className="w-4 h-4 mr-2" />
                  Go to Privacy Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicGDPRPage;